import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import UserTable from './components/UserTable';
import UserForm from './components/UserForm';
import UserProfile from './components/UserProfile';
import BulkActionModal from './components/BulkActionModal';
import QRCodeModal from './components/QRCodeModal';
import { userService } from '../../services/userService';

const UserManagement = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [bulkAction, setBulkAction] = useState('');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    pending: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load users and stats from Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const [usersData, statsData] = await Promise.all([
          userService?.getUsers(),
          userService?.getUserStats()
        ]);
        
        setUsers(usersData);
        setStats(statsData);
      } catch (err) {
        console.error('Failed to load user data:', err);
        setError('Failed to load user data. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const handleMenuToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setShowUserForm(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowUserForm(true);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserProfile(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUsers([user?.id]);
    setBulkAction('delete');
    setShowBulkModal(true);
  };

  const handleUserSave = async (userData) => {
    try {
      let savedUser;
      if (selectedUser) {
        // Update existing user
        savedUser = await userService?.updateUser(selectedUser?.id, userData);
        setUsers(prev => prev?.map(user => 
          user?.id === selectedUser?.id ? savedUser : user
        ));
      } else {
        // Create new user
        savedUser = await userService?.createUser(userData);
        setUsers(prev => [...prev, savedUser]);
        
        // Show QR code if requested
        if (userData?.generateQR) {
          setSelectedUser(savedUser);
          setShowQRModal(true);
        }
      }
      
      // Refresh stats
      const statsData = await userService?.getUserStats();
      setStats(statsData);
      
      setShowUserForm(false);
    } catch (error) {
      console.error('Error saving user:', error);
      setError('Failed to save user. Please try again.');
    }
  };

  const handleBulkAction = (action) => {
    setBulkAction(action);
    setShowBulkModal(true);
  };

  const handleBulkConfirm = async (action) => {
    try {
      switch (action) {
        case 'activate':
          await userService?.activateUsers(selectedUsers);
          setUsers(prev => prev?.map(user => 
            selectedUsers?.includes(user?.id) 
              ? { ...user, status: 'active', isActive: true }
              : user
          ));
          break;
        case 'deactivate':
          await userService?.deactivateUsers(selectedUsers);
          setUsers(prev => prev?.map(user => 
            selectedUsers?.includes(user?.id) 
              ? { ...user, status: 'inactive', isActive: false }
              : user
          ));
          break;
        case 'delete':
          await userService?.deleteUsers(selectedUsers);
          setUsers(prev => prev?.map(user => 
            selectedUsers?.includes(user?.id) 
              ? { ...user, status: 'inactive', isActive: false }
              : user
          ));
          break;
      }
      
      // Refresh stats
      const statsData = await userService?.getUserStats();
      setStats(statsData);
      
      setSelectedUsers([]);
      setShowBulkModal(false);
    } catch (error) {
      console.error('Bulk action failed:', error);
      setError('Bulk action failed. Please try again.');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          isOpen={sidebarOpen} 
          onClose={handleSidebarClose} 
        />
        <main className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        }`}>
          <div className="p-6">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-destructive mb-2">Error Loading Users</h3>
              <p className="text-destructive/80">{error}</p>
              <button 
                onClick={() => window.location?.reload()} 
                className="mt-3 px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        isOpen={sidebarOpen} 
        onClose={handleSidebarClose} 
      />
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="p-6 space-y-6">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">User Management</h1>
              <p className="text-muted-foreground">
                {isLoading 
                  ? "Loading user data..." 
                  : `Manage user accounts, roles, and permissions - ${stats?.total} total users`
                }
              </p>
            </div>
            <Button
              onClick={handleCreateUser}
              iconName="UserPlus"
              iconPosition="left"
              disabled={isLoading}
            >
              Create User
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-card-foreground">
                    {isLoading ? "..." : stats?.total}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-card-foreground">
                    {isLoading ? "..." : stats?.active}
                  </p>
                  <p className="text-sm text-muted-foreground">Active Users</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-warning" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-card-foreground">
                    {isLoading ? "..." : stats?.pending}
                  </p>
                  <p className="text-sm text-muted-foreground">Pending</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                  <Icon name="XCircle" size={20} className="text-muted-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-card-foreground">
                    {isLoading ? "..." : stats?.inactive}
                  </p>
                  <p className="text-sm text-muted-foreground">Inactive</p>
                </div>
              </div>
            </div>
          </div>

          {/* User Table */}
          <UserTable
            users={users}
            selectedUsers={selectedUsers}
            onSelectionChange={setSelectedUsers}
            onUserSelect={handleViewUser}
            onUserEdit={handleEditUser}
            onUserDelete={handleDeleteUser}
            onBulkAction={handleBulkAction}
            isLoading={isLoading}
          />
        </div>
      </main>
      {/* Modals */}
      <UserForm
        user={selectedUser}
        isOpen={showUserForm}
        onSave={handleUserSave}
        onCancel={() => setShowUserForm(false)}
      />
      <UserProfile
        user={selectedUser}
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
        onEdit={handleEditUser}
      />
      <BulkActionModal
        isOpen={showBulkModal}
        action={bulkAction}
        selectedCount={selectedUsers?.length}
        onClose={() => setShowBulkModal(false)}
        onConfirm={handleBulkConfirm}
      />
      <QRCodeModal
        isOpen={showQRModal}
        user={selectedUser}
        onClose={() => setShowQRModal(false)}
      />
    </div>
  );
};

export default UserManagement;